import './App.css'
import frFR from "antd/locale/fr_FR";
import { ConfigProvider, theme, Spin } from 'antd';
import { Route, Routes, BrowserRouter as Router } from "react-router-dom";
import Moment from 'react-moment';
import 'moment/locale/fr';
import moment from 'moment-timezone';
import React from 'react';
import { useUserContext } from './contexts/UserContext';
import { useNotificationContext } from './contexts/NotificationContext';
import { usePusher } from './contexts/PusherContext';
import 'react-phone-number-input/style.css'
import { routes } from './helpers/routes';
import Toast from './helpers/Toast';
import PWAInstallPrompt from './components/utils/PWAInstallPrompt';
import { useTheme } from './contexts/ThemeContext';
import { AiChatProvider } from './contexts/AiChatContext';

// Sets the moment instance to use.
Moment.globalMoment = moment;
// Set the locale for every react-moment instance to French.
Moment.globalLocale = 'fr';
// Set the output format for every react-moment instance.
Moment.globalFormat = 'D MMM YYYY';
// Set time zone for every react-moment instance : Africa/Dakar
Moment.globalTimezone = 'Africa/Dakar';
// Set the output timezone for local for every instance.
Moment.globalLocal = true;
// Use a <span> tag for every react-moment instance.
Moment.globalElement = 'span';
const primaryColor = "#3E65AC";

function App() {
  const { isDark } = useTheme();
  const { getCurrentUser, currentUser, check, loadingCurrentUser } = useUserContext();
  const { loadUnreadNotificationsCount, loadUnreadNotifications } = useNotificationContext();

  const { pusher, userPrivateChannelName } = usePusher();

  React.useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  React.useEffect(() => {
    if (currentUser) {
      loadUnreadNotificationsCount();
      loadUnreadNotifications();
    }
  }, [currentUser, loadUnreadNotificationsCount, loadUnreadNotifications]);

  const handleNotificationEvent = React.useCallback((data) => {
    Toast.success(data.title);
    loadUnreadNotificationsCount();
    loadUnreadNotifications();
  }, [loadUnreadNotificationsCount, loadUnreadNotifications]);

  React.useEffect(() => {
    if (currentUser && pusher) {
      const channelName = userPrivateChannelName;
      console.log(`Subscribing to channel: ${channelName}`);
      const channel = pusher.subscribe(channelName);

      const events = [
        'new-responsable-affaire',
        'new-affaire-member',
        'new-responsable-tache',
        'affaire-closed',
        'tache-closed',
        'new-comment',
        "new-piece-jointe",
        "new-tache-member",
      ];

      events.forEach((event) => {
        channel.bind(event, handleNotificationEvent);
      });

      return () => {
        channel.unbind_all();
        channel.unsubscribe();
      };
    }
  }, [pusher, currentUser, userPrivateChannelName, handleNotificationEvent]);

  if (loadingCurrentUser) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <ConfigProvider locale={frFR}
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: primaryColor,
          borderRadius: 3,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif',
          fontSize: 14,
          // Backgrounds
          colorBgContainer:    isDark ? '#1D2125' : '#FFFFFF',
          colorBgElevated:     isDark ? '#22272B' : '#FFFFFF',
          colorBgLayout:       isDark ? '#161A1D' : '#F4F6FA',
          colorBgSpotlight:    isDark ? '#282E33' : '#FFFFFF',
          // Text
          colorText:           isDark ? '#B6C2CF' : '#172B4D',
          colorTextSecondary:  isDark ? '#8C9BAB' : '#44546F',
          colorTextDisabled:   isDark ? '#596773' : '#8590A2',
          colorTextHeading:    isDark ? '#B6C2CF' : '#172B4D',
          // Borders
          colorBorder:         isDark ? '#2C3438' : '#DCDFE4',
          colorBorderSecondary: isDark ? '#2C3438' : '#F1F2F4',
          // Fill
          colorFill:           isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          colorFillSecondary:  isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          // Semantic
          colorSuccess:        isDark ? '#4BCE97' : '#1F845A',
          colorWarning:        isDark ? '#F5CD47' : '#B25000',
          colorError:          isDark ? '#F87462' : '#CA3521',
          colorInfo:           primaryColor,
        },
        components: {
          Menu: {
            colorPrimary: primaryColor,
            borderRadius: 3,
            colorBgContainer: isDark ? '#1D2125' : '#FFFFFF',
            colorItemText: isDark ? '#B6C2CF' : '#172B4D',
            colorItemTextHover: isDark ? '#B6C2CF' : '#172B4D',
            colorItemBgHover: isDark ? 'rgba(255,255,255,0.06)' : '#F1F2F4',
          },
          Table: {
            headerBg:            isDark ? '#22272B' : '#F1F2F4',
            headerColor:         isDark ? '#8C9BAB' : '#44546F',
            headerSortActiveBg:  isDark ? '#282E33' : '#E8ECF0',
            headerSortHoverBg:   isDark ? '#282E33' : '#E8ECF0',
            rowHoverBg:          isDark ? 'rgba(255,255,255,0.04)' : '#F7F8F9',
            borderColor:         isDark ? '#2C3438' : '#DCDFE4',
          },
          Progress: {
            defaultColor: primaryColor,
          },
          Alert: {
            colorInfo: primaryColor,
          },
          Button: {
            borderRadius: 3,
            fontWeight: 500,
          },
          Input: {
            borderRadius: 3,
            colorBgContainer: isDark ? '#22272B' : '#FFFFFF',
            colorBorder: isDark ? '#2C3438' : '#DCDFE4',
          },
          Select: {
            borderRadius: 3,
            colorBgContainer: isDark ? '#22272B' : '#FFFFFF',
            colorBorder: isDark ? '#2C3438' : '#DCDFE4',
          },
          DatePicker: {
            borderRadius: 3,
            colorBgContainer: isDark ? '#22272B' : '#FFFFFF',
          },
          Modal: {
            colorBgElevated: isDark ? '#282E33' : '#FFFFFF',
            borderRadius: 4,
          },
          Card: {
            colorBgContainer: isDark ? '#1D2125' : '#FFFFFF',
            borderRadius: 4,
          },
          Tabs: {
            colorPrimary: primaryColor,
            inkBarColor: primaryColor,
          },
          Steps: {
            colorPrimary: primaryColor,
          },
          Tag: {
            borderRadius: 3,
          },
          Badge: {
            borderRadius: 10,
          },
        }
      }}
    >
      <AiChatProvider>
        <Router>
          <Routes>
            {
              routes.filter((route) => route.permission === '' || check(route.permission))
                .map((route, index) => (
                  <Route key={index} path={route.path} element={<route.component />} />
                ))
            }
          </Routes>
        </Router>
        <PWAInstallPrompt />
      </AiChatProvider>
    </ConfigProvider>
  );
}

export default App
